import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {cdnImage} from '@/util/cdnImage';
import {borderRadius, colors, fontSize, fontWeight, zIndex} from '@/styles/tokens';
import {imgSizes} from '@/util/imageSizes';

const officeImage = cdnImage('about-us-new/Debut-building-image.webp');

const AboutSection = () => {
    return (
        <Box component="section" className="top-space mobile-top-padding bottom-spacing motbile-padding-bottom">
            <MUIContainer>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', md: 'row'},
                        alignItems: 'center',
                        gap: {xs: 4, md: 0},
                        position: 'relative'
                    }}
                >
                    {/* Image — actual aspect ratio 934×792 ≈ 1.179:1 */}
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: {xs: '100%', md: '480px'},
                            aspectRatio: '934 / 792',
                            position: 'relative',
                            borderRadius: borderRadius['2xl'],
                            overflow: 'hidden',
                            zIndex: 2
                        }}
                    >
                        <Image
                            src={officeImage}
                            alt="Debut Infotech Office Building"
                            fill
                            sizes={imgSizes({xs: '100vw', md: '580px'})}
                            style={{objectFit: 'cover', objectPosition: 'center'}}
                            priority
                        />
                    </Box>

                    {/* Rounded trapezoid background + text */}
                    <Box
                        sx={{
                            flex: 1,
                            position: 'relative',
                            ml: {xs: 0, md: '-100px'},
                            pl: {xs: 3, md: '140px'},
                            pr: {xs: 3, md: 7},
                            py: {xs: 4, md: 8},
                            zIndex: zIndex.raised
                        }}
                    >
                        {/*
                          viewBox 200×100 matches ~2:1 container so 1 SVG unit ≈ 1 visual unit.
                          Trapezoid corners: TL(10,6) TR(194,1) BR(194,99) BL(4,94)
                          Left edge unit vector (up): (0.068, -0.998)
                          Top  edge unit vector (right): (0.9993, -0.027)
                          r = 10 → all four corners are properly tangent-matched.
                        */}
                        <Box
                            component="svg"
                            viewBox="0 0 200 100"
                            preserveAspectRatio="none"
                            xmlns="http://www.w3.org/2000/svg"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: -1,
                                display: {xs: 'none', md: 'block'}
                            }}
                        >
                            <path
                                d="M 20,5.7 L 184,0.7 Q 194,1 194,11 L 194,89 Q 194,99 184,98.7 L 14,94.3 Q 4,94 4.7,84 L 9.3,16 Q 10,6 20,5.7 Z"
                                fill={colors.background.tag}
                            />
                        </Box>

                        {/* Mobile fallback */}
                        <Box
                            sx={{
                                display: {xs: 'block', md: 'none'},
                                position: 'absolute',
                                inset: 0,
                                backgroundColor: colors.background.tag,
                                borderRadius: borderRadius['2xl'],
                                zIndex: -1
                            }}
                        />

                        <Typography
                            component="h2"
                            className="font-headers"
                            sx={{
                                fontSize: {xs: fontSize[28], md: fontSize[32]},
                                fontWeight: fontWeight.extrabold,
                                color: colors.text.primary,
                                mb: 3,
                                lineHeight: 1.2
                            }}
                        >
                            About Debut Infotech
                        </Typography>

                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <Typography
                                className="font-regular-text"
                                sx={{fontSize: fontSize[16], color: colors.text.primary, lineHeight: 1.8}}
                            >
                                Debut Infotech has partnered with startups and enterprises to turn business ideas into digital products that
                                solve real challenges. We take the time to understand each client&apos;s goals and business requirements
                                before shaping solutions that support their plans for growth. Our focus is on building technology that
                                serves a clear purpose and delivers lasting value.
                            </Typography>

                            <Typography
                                className="font-regular-text"
                                sx={{fontSize: fontSize[16], color: colors.text.primary, lineHeight: 1.8}}
                            >
                                Our expertise spans AI-powered products, blockchain solutions, web and mobile applications, and custom
                                software development. Whether a business is launching a new product or improving an existing platform, we
                                work to create solutions that are reliable, secure, and ready to scale as its needs evolve. We bring
                                technology and business objectives together to help organizations make the most of their digital
                                investments.
                            </Typography>

                            <Typography
                                className="font-regular-text"
                                sx={{fontSize: fontSize[16], color: colors.text.primary, lineHeight: 1.8}}
                            >
                                We believe successful digital products are built through strong collaboration and thoughtful execution. From
                                understanding the initial requirements to development, testing, and launch, our team works closely with
                                clients throughout the journey. We value clear communication and practical decision-making so businesses can
                                move forward with greater confidence and build a strong foundation for future opportunities.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(AboutSection);
